import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { CouponService } from '../../../_services/coupon.service';
import { AlertService } from '../../../_services/alert.service';
import { Coupon } from '../../../_interfaces/coupons';
import { AddEditCouponsComponent } from '../add-edit-coupons/add-edit-coupons.component';
import { RecordNavComponent, confirmLeave } from '../../../../Shared/_components/record-nav/record-nav.component';
import type { CouponListState } from '../discount-coupons.component';

// A coupon on its own page: /discount-coupons/create (?copy=<id> starts from
// a copy), /discount-coupons/edit/:id. The form itself is the same component
// the dialog uses, in page mode. Previous / Next follow the Discount Coupons
// list (its search, filters, sort and page come along in the address).
@Component({
    selector: 'app-coupon-edit',
    imports: [CommonModule, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, AddEditCouponsComponent, RecordNavComponent],
    templateUrl: './coupon-edit.component.html'
})
export class CouponEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private couponService = inject(CouponService);
  private alertService = inject(AlertService);

  @ViewChild(AddEditCouponsComponent) form?: AddEditCouponsComponent;

  couponId = 0;
  copyOf = 0;
  listState: CouponListState = {};
  loading = true;
  notFound = false;
  coupon: Coupon | null = null;
  formData: { type: 'ADD' | 'EDIT'; cuopon?: any } | null = null;

  // ---- previous / next (the list's pages, fetched as needed) ----
  private pages = new Map<number, Coupon[]>();
  private navPage = 0;
  private pageSize = 10;
  navTotal = 0;

  get isEdit(): boolean {
    return this.couponId > 0;
  }

  ngOnInit(): void {
    this.couponId = Number(this.route.snapshot.paramMap.get('id')) || 0;
    const qp = this.route.snapshot.queryParamMap;
    for (const k of ['q', 'status', 'kind', 'sort', 'page', 'size'] as const) {
      const v = qp.get(k);
      if (v !== null) this.listState[k] = v;
    }
    this.copyOf = Number(qp.get('copy')) || 0;
    this.navPage = Number(this.listState.page) || 0;
    this.pageSize = Number(this.listState.size) || 10;

    const sourceId = this.couponId || this.copyOf;
    if (!sourceId) {
      this.formData = { type: 'ADD' };
      this.loading = false;
      return;
    }
    // Not `lite`: the form needs the coupon's categories and products.
    this.couponService.getAllCoupons({ id: sourceId, pageIndex: 0, pageSize: 1 }).subscribe({
      next: (r: any) => {
        this.coupon = r?.couponCodes?.[0] ?? null;
        this.notFound = !this.coupon;
        if (this.coupon) this.formData = { type: this.isEdit ? 'EDIT' : 'ADD', cuopon: this.coupon };
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
      },
    });
    if (this.isEdit) this.loadNavPage(this.navPage, true);
  }

  // ---- previous / next ----
  private loadNavPage(page: number, findNeighbours = false): void {
    if (page < 0 || this.pages.has(page)) return;
    const s = this.listState;
    this.couponService
      .getAllCoupons({ keywordSearch: s.q || '', pageIndex: page, pageSize: this.pageSize, status: s.status || '', kind: s.kind || '', sort: s.sort || 'newest', lite: true })
      .subscribe({
        next: (r: any) => {
          const rows: Coupon[] = r?.couponCodes ?? [];
          this.pages.set(page, rows);
          this.navTotal = r?.count ?? 0;
          if (!findNeighbours) return;
          const i = rows.findIndex((c) => c.id === this.couponId);
          if (i === 0) this.loadNavPage(page - 1);
          if (i === rows.length - 1 && (page + 1) * this.pageSize < this.navTotal) this.loadNavPage(page + 1);
        },
        error: () => {},
      });
  }

  private get navRows(): Coupon[] {
    return this.pages.get(this.navPage) ?? [];
  }

  private get navIndex(): number {
    return this.navRows.findIndex((c) => c.id === this.couponId);
  }

  get position(): number {
    return this.navIndex >= 0 ? this.navPage * this.pageSize + this.navIndex : -1;
  }

  get prev(): { c: Coupon; page: number } | null {
    const i = this.navIndex;
    if (i < 0) return null;
    if (i > 0) return { c: this.navRows[i - 1], page: this.navPage };
    const before = this.pages.get(this.navPage - 1);
    return before?.length ? { c: before[before.length - 1], page: this.navPage - 1 } : null;
  }

  get next(): { c: Coupon; page: number } | null {
    const i = this.navIndex;
    if (i < 0) return null;
    if (i < this.navRows.length - 1) return { c: this.navRows[i + 1], page: this.navPage };
    const after = this.pages.get(this.navPage + 1);
    return after?.length ? { c: after[0], page: this.navPage + 1 } : null;
  }

  private get dirty(): boolean {
    return !!this.form?.hasChanges;
  }

  goTo(target: { c: Coupon; page: number } | null): void {
    if (!target) return;
    confirmLeave(this.dialog, this.dirty, this.coupon?.couponCode ?? '', target.c.couponCode).subscribe((ok) => {
      if (!ok) return;
      this.router.navigate(['/discount-coupons/edit', target.c.id], { queryParams: { ...this.listState, page: target.page ? String(target.page) : undefined } });
    });
  }

  backToList(force = false): void {
    const go = () => this.router.navigate(['/discount-coupons'], { queryParams: { ...this.listState, page: this.navPage ? String(this.navPage) : undefined } });
    if (force) return void go();
    confirmLeave(this.dialog, this.dirty, this.coupon?.couponCode ?? 'this coupon', 'the list').subscribe((ok) => ok && go());
  }

  // The form reports back: saved -> back to the list; cancelled -> back too (asking if unsaved).
  onDone(result: { status: boolean }): void {
    if (result.status) {
      if (this.isEdit) {
        // Stay put so Previous / Next keep going; the form is now saved.
        this.alertService.alert(`${this.coupon?.couponCode ?? 'Coupon'} saved`, 'success');
      } else {
        this.backToList(true);
      }
    } else {
      this.backToList();
    }
  }

  valueLabel(c: Coupon): string {
    const v = Number(c.couponValue) || 0;
    const text = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    return c.couponPerc ? `${text}%` : `£${text}`;
  }
}
